import { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
// Tabs removed - using simplified layout
import { Badge } from '@/components/ui/badge'
import {
  Sparkles,
  Play,
  Save,
  Trash2,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  Loader2,
  RefreshCw,
  Settings,
  User,
  X,
} from 'lucide-react'
import { formatCurrency } from '@/lib/utils'
import api from '@/lib/api'
import type {
  Scenario,
  ScenarioSpec,
  ScenarioTemplate,
  ProjectionResult,
  ScenarioEffect,
  BaselineProjectionResponse,
  AppliedFactor,
} from '@/types'
import { useViewModeStore } from '@/stores/viewModeStore'
import { ScenarioOnboarding } from '@/components/scenarios/ScenarioOnboarding'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
} from 'chart.js'
import { Line } from 'react-chartjs-2'

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend
)

interface ScenarioSuggestion {
  id: string
  title: string
  description: string
  category: string
  spec: ScenarioSpec
}

export function ScenariosPage() {
  const viewMode = useViewModeStore((state) => state.viewMode)
  const [activeTab, setActiveTab] = useState<'create' | 'saved'>('create')
  const [scenarioText, setScenarioText] = useState('')
  const [isParsing, setIsParsing] = useState(false)
  const [isRunning, setIsRunning] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true)
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(true)
  const [isLoadingSaved, setIsLoadingSaved] = useState(false)
  const [isLoadingBaseline, setIsLoadingBaseline] = useState(false)

  const [baselineProjection, setBaselineProjection] = useState<BaselineProjectionResponse | null>(null)
  const [appliedFactors, setAppliedFactors] = useState<AppliedFactor[]>([])
  const [scenarioProjection, setScenarioProjection] = useState<ProjectionResult | null>(null)
  const [_isRunningScenario, setIsRunningScenario] = useState(false)

  const [templates, setTemplates] = useState<ScenarioTemplate[]>([])
  const [suggestions, setSuggestions] = useState<ScenarioSuggestion[]>([])
  const [savedScenarios, setSavedScenarios] = useState<Scenario[]>([])
  const [currentSpec, setCurrentSpec] = useState<ScenarioSpec | null>(null)
  const [stackedSpecs, setStackedSpecs] = useState<ScenarioSpec[]>([]) // Track individual scenarios for deletion
  const [projectionResult, setProjectionResult] = useState<ProjectionResult | null>(null)
  const [explanation, setExplanation] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [clarificationAnswers, setClarificationAnswers] = useState<Record<number, string>>({})
  const [isRefining, setIsRefining] = useState(false)

  // Income and expense assumptions
  const [yearlyIncome, setYearlyIncome] = useState<number>(0)
  const [state, setState] = useState<string>('CA')
  const [monthlyExpense, setMonthlyExpense] = useState<number>(0)
  const [_monthlyIncomeAfterTax, setMonthlyIncomeAfterTax] = useState<number>(0)
  const [isLoadingDefaults, setIsLoadingDefaults] = useState(true)
  const [showOnboarding, setShowOnboarding] = useState(false)

  // Load default income/expense from user data
  useEffect(() => {
    async function loadDefaults() {
      try {
        const response = await api.get('/scenarios/defaults', { params: { view: viewMode } })
        console.log('[Scenarios] defaults response:', response.data)
        setYearlyIncome(response.data.yearly_income || 0)
        setState(response.data.state || 'CA')
        setMonthlyExpense(response.data.monthly_expense || 0)
        setMonthlyIncomeAfterTax(response.data.monthly_income_after_tax || 0)

        // Show onboarding if no settings saved
        if (!response.data.has_settings) {
          console.log('[Scenarios] No settings found, showing onboarding')
          setShowOnboarding(true)
        } else {
          console.log('[Scenarios] Settings found, loading baseline projection')
          // Load baseline projection for existing users
          loadBaselineProjection(
            response.data.yearly_income,
            response.data.monthly_expense,
            response.data.state,
            response.data.is_after_tax || false
          )
        }
      } catch (err) {
        console.error('Failed to load defaults:', err)
      } finally {
        setIsLoadingDefaults(false)
      }
    }
    loadDefaults()
  }, [viewMode])

  // Handle onboarding completion
  const handleOnboardingComplete = (settings: {
    yearly_income: number
    state: string
    monthly_expense: number
    monthly_income_after_tax: number
    applied_factors?: Array<{ id: string; title: string; effects: ScenarioEffect[] }>
  }) => {
    setYearlyIncome(settings.yearly_income)
    setState(settings.state)
    setMonthlyExpense(settings.monthly_expense)
    setMonthlyIncomeAfterTax(settings.monthly_income_after_tax)
    setShowOnboarding(false)

    // If user applied factors during onboarding, create and run a combined scenario
    if (settings.applied_factors && settings.applied_factors.length > 0) {
      // Create individual specs for tracking
      const individualSpecs: ScenarioSpec[] = settings.applied_factors.map(f => ({
        title: f.title,
        time_horizon_months: 24,
        assumptions: [],
        effects: f.effects,
        needs_clarification: false,
        clarifying_questions: []
      }))
      setStackedSpecs(individualSpecs)

      const combinedSpec: ScenarioSpec = {
        title: settings.applied_factors.map(f => f.title).join(' + '),
        time_horizon_months: 24,
        assumptions: [],
        effects: settings.applied_factors.flatMap(f => f.effects),
        needs_clarification: false,
        clarifying_questions: []
      }
      setCurrentSpec(combinedSpec)
      // Auto-run the projection
      runProjection(combinedSpec)
    }
  }

  // Update after-tax income when income or state changes
  const updateTaxCalculation = useCallback(async () => {
    if (yearlyIncome > 0) {
      try {
        const response = await api.post('/scenarios/estimate-tax', {
          yearly_income: yearlyIncome,
          state: state
        })
        setMonthlyIncomeAfterTax(response.data.monthly_income_after_tax)
      } catch (err) {
        console.error('Failed to estimate tax:', err)
      }
    }
  }, [yearlyIncome, state])

  useEffect(() => {
    const timer = setTimeout(updateTaxCalculation, 500)
    return () => clearTimeout(timer)
  }, [updateTaxCalculation])

  // Load templates on mount
  useEffect(() => {
    async function loadTemplates() {
      try {
        const response = await api.get('/scenarios/templates')
        setTemplates(response.data.templates || [])
      } catch (err) {
        console.error('Failed to load templates:', err)
      } finally {
        setIsLoadingTemplates(false)
      }
    }
    loadTemplates()
  }, [])

  // Load personalized suggestions
  const loadSuggestions = useCallback(async (regenerate = false) => {
    setIsLoadingSuggestions(true)
    try {
      const response = await api.get('/scenarios/suggestions', {
        params: { view: viewMode, regenerate: regenerate ? 'true' : 'false' }
      })
      setSuggestions(response.data.suggestions || [])
    } catch (err) {
      console.error('Failed to load suggestions:', err)
    } finally {
      setIsLoadingSuggestions(false)
    }
  }, [viewMode])

  useEffect(() => {
    loadSuggestions()
  }, [loadSuggestions])

  // Load baseline projection
  const loadBaselineProjection = useCallback(async (
    income: number,
    expense: number,
    stateCode: string,
    isAfterTax: boolean
  ) => {
    setIsLoadingBaseline(true)
    try {
      const response = await api.post('/scenarios/baseline-projection', {
        yearly_income: income,
        monthly_expense: expense,
        state: stateCode,
        is_after_tax: isAfterTax,
        view: viewMode,
        time_horizon_months: 24
      })
      setBaselineProjection(response.data)
    } catch (err) {
      console.error('Failed to load baseline projection:', err)
    } finally {
      setIsLoadingBaseline(false)
    }
  }, [viewMode])

  // Remove a scenario factor
  const removeFactor = useCallback(async (factorId: string) => {
    const updatedFactors = appliedFactors.filter(f => f.id !== factorId)
    setAppliedFactors(updatedFactors)

    if (updatedFactors.length === 0) {
      setScenarioProjection(null)
      return
    }

    // Re-run projection with remaining factors
    setIsRunningScenario(true)
    try {
      const allEffects = updatedFactors.flatMap(f => f.effects)
      const response = await api.post('/scenarios/run-adhoc', {
        spec: {
          title: updatedFactors.map(f => f.title).join(' + '),
          time_horizon_months: 24,
          effects: allEffects
        },
        view: viewMode,
        yearly_income: yearlyIncome,
        state: state,
        monthly_expense: monthlyExpense
      })
      setScenarioProjection(response.data.result)
    } catch (err) {
      console.error('Failed to run scenario:', err)
    } finally {
      setIsRunningScenario(false)
    }
  }, [appliedFactors, viewMode, yearlyIncome, state, monthlyExpense])

  // Load saved scenarios when switching to saved tab
  const loadSavedScenarios = useCallback(async () => {
    setIsLoadingSaved(true)
    try {
      const response = await api.get('/scenarios')
      setSavedScenarios(response.data.scenarios || [])
    } catch (err) {
      console.error('Failed to load saved scenarios:', err)
    } finally {
      setIsLoadingSaved(false)
    }
  }, [])

  useEffect(() => {
    if (activeTab === 'saved') {
      loadSavedScenarios()
    }
  }, [activeTab, loadSavedScenarios])

  // Parse scenario text with LLM
  const handleParse = async () => {
    if (!scenarioText.trim()) return

    // If we already have a scenario result, add to it instead of replacing
    if (projectionResult && currentSpec) {
      await addFactorToScenario(scenarioText)
      return
    }

    setIsParsing(true)
    setError(null)
    setCurrentSpec(null)
    setStackedSpecs([]) // Reset stacked specs
    setProjectionResult(null)
    setExplanation(null)
    setClarificationAnswers({})

    try {
      const response = await api.post('/scenarios/parse', {
        text: scenarioText,
        view: viewMode,
        time_horizon_months: 24,
      })
      setCurrentSpec(response.data.spec)
      setStackedSpecs([response.data.spec]) // Initialize with first scenario

      // Auto-run if no clarification needed
      if (!response.data.spec.needs_clarification) {
        await runProjection(response.data.spec)
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to parse scenario'
      setError(errorMessage)
    } finally {
      setIsParsing(false)
    }
  }

  // Add a new factor to an existing scenario
  const addFactorToScenario = async (text: string) => {
    if (!currentSpec) return

    setIsParsing(true)
    setError(null)

    try {
      // Parse the new factor
      const response = await api.post('/scenarios/parse', {
        text: text,
        view: viewMode,
        time_horizon_months: currentSpec.time_horizon_months || 24,
      })
      const newSpec = response.data.spec as ScenarioSpec

      // Add to stacked specs
      const updatedStacked = [...stackedSpecs, newSpec]
      setStackedSpecs(updatedStacked)

      // Merge effects from all stacked specs
      const mergedEffects = updatedStacked.flatMap(s => s.effects || [])
      const mergedAssumptions = updatedStacked.flatMap(s => s.assumptions || [])

      // Create combined spec
      const combinedSpec: ScenarioSpec = {
        title: updatedStacked.map(s => s.title).join(' + '),
        time_horizon_months: currentSpec.time_horizon_months,
        assumptions: mergedAssumptions,
        effects: mergedEffects,
        needs_clarification: false,
        clarifying_questions: [],
      }

      setCurrentSpec(combinedSpec)
      setScenarioText('')

      // Run the combined scenario
      await runProjection(combinedSpec)
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add factor'
      setError(errorMessage)
    } finally {
      setIsParsing(false)
    }
  }

  // Add a template/suggestion as a factor to existing scenario
  const addTemplateAsFactorToScenario = async (template: ScenarioTemplate) => {
    if (!currentSpec) return

    setIsRunning(true)
    setError(null)

    try {
      // Add template spec to stacked specs
      const updatedStacked = [...stackedSpecs, template.spec]
      setStackedSpecs(updatedStacked)

      // Merge effects from all stacked specs
      const mergedEffects = updatedStacked.flatMap(s => s.effects || [])
      const mergedAssumptions = updatedStacked.flatMap(s => s.assumptions || [])

      const combinedSpec: ScenarioSpec = {
        title: updatedStacked.map(s => s.title).join(' + '),
        time_horizon_months: currentSpec.time_horizon_months,
        assumptions: mergedAssumptions,
        effects: mergedEffects,
        needs_clarification: false,
        clarifying_questions: [],
      }

      setCurrentSpec(combinedSpec)
      await runProjection(combinedSpec)
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add factor'
      setError(errorMessage)
    } finally {
      setIsRunning(false)
    }
  }

  // Remove a stacked scenario and re-run projection
  const removeStackedScenario = async (index: number) => {
    const updatedStacked = stackedSpecs.filter((_, i) => i !== index)
    setStackedSpecs(updatedStacked)

    if (updatedStacked.length === 0) {
      // Clear everything if no scenarios left
      setProjectionResult(null)
      setCurrentSpec(null)
      setExplanation(null)
      return
    }

    // Re-merge remaining specs
    const mergedEffects = updatedStacked.flatMap(s => s.effects || [])
    const mergedAssumptions = updatedStacked.flatMap(s => s.assumptions || [])

    const combinedSpec: ScenarioSpec = {
      title: updatedStacked.map(s => s.title).join(' + '),
      time_horizon_months: updatedStacked[0].time_horizon_months || 24,
      assumptions: mergedAssumptions,
      effects: mergedEffects,
      needs_clarification: false,
      clarifying_questions: [],
    }

    setCurrentSpec(combinedSpec)
    await runProjection(combinedSpec)
  }

  // Run projection
  const runProjection = async (spec: ScenarioSpec) => {
    setIsRunning(true)
    setError(null)

    try {
      const response = await api.post('/scenarios/run-adhoc', {
        spec,
        view: viewMode,
        yearly_income: yearlyIncome,
        state: state,
        monthly_expense: monthlyExpense,
      })
      setProjectionResult(response.data.result)
      setExplanation(response.data.explanation || null)
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to run projection'
      setError(errorMessage)
    } finally {
      setIsRunning(false)
    }
  }

  // Use a template (or add to existing scenario)
  const handleUseTemplate = async (template: ScenarioTemplate) => {
    // If we already have a scenario result, add this as a factor
    if (projectionResult && currentSpec) {
      await addTemplateAsFactorToScenario(template)
      return
    }

    setCurrentSpec(template.spec)
    setStackedSpecs([template.spec]) // Initialize with first scenario
    setScenarioText('') // Clear input - don't show template description
    setError(null)
    setClarificationAnswers({})

    if (!template.spec.needs_clarification) {
      await runProjection(template.spec)
    }
  }

  // Refine scenario with clarification answers
  const handleRefineWithAnswers = async () => {
    if (!currentSpec || !scenarioText.trim()) return

    setIsRefining(true)
    setError(null)

    try {
      // Build context string from answers, handling both string and object question formats
      const answersContext = currentSpec.clarifying_questions
        .map((q, i) => {
          const isObjectQuestion = typeof q === 'object' && q !== null
          const questionText = isObjectQuestion ? q.question : q
          const suggestedValue = isObjectQuestion ? q.suggested_value : undefined
          // Use user's answer if provided, otherwise use suggested value
          const answer = clarificationAnswers[i] ?? (suggestedValue !== undefined ? String(suggestedValue) : 'Not provided')
          return `${questionText} Answer: ${answer}`
        })
        .join('\n')

      const response = await api.post('/scenarios/parse', {
        text: `${scenarioText}\n\nAdditional context:\n${answersContext}`,
        view: viewMode,
        time_horizon_months: currentSpec.time_horizon_months,
      })
      setCurrentSpec(response.data.spec)
      setStackedSpecs([response.data.spec]) // Initialize with refined scenario
      setClarificationAnswers({})

      // Auto-run if no more clarification needed
      if (!response.data.spec.needs_clarification) {
        await runProjection(response.data.spec)
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to refine scenario'
      setError(errorMessage)
    } finally {
      setIsRefining(false)
    }
  }

  // Save scenario
  const handleSave = async () => {
    if (!currentSpec) return

    setIsSaving(true)
    try {
      await api.post('/scenarios', { spec: currentSpec })
      // Refresh saved scenarios
      await loadSavedScenarios()
      setActiveTab('saved')
    } catch (err) {
      console.error('Failed to save scenario:', err)
    } finally {
      setIsSaving(false)
    }
  }

  // Delete scenario
  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/scenarios/${id}`)
      setSavedScenarios((prev) => prev.filter((s) => s.id !== id))
    } catch (err) {
      console.error('Failed to delete scenario:', err)
    }
  }

  // Run a saved scenario
  const handleRunSaved = async (scenario: Scenario) => {
    setCurrentSpec(scenario.spec)
    setStackedSpecs([scenario.spec]) // Initialize with saved scenario
    setActiveTab('create')
    await runProjection(scenario.spec)
  }

  // Chart configuration
  const chartData = projectionResult
    ? {
        labels: projectionResult.baseline_series.map((p) => `M${p.month}`),
        datasets: [
          {
            label: 'Baseline',
            data: projectionResult.baseline_series.map((p) => p.value),
            borderColor: '#94a3b8',
            backgroundColor: 'rgba(148, 163, 184, 0.1)',
            borderDash: [5, 5],
            tension: 0.4,
            pointRadius: 0,
            pointHoverRadius: 4,
          },
          {
            label: 'Scenario',
            data: projectionResult.scenario_series.map((p) => p.value),
            borderColor: projectionResult.impact >= 0 ? '#10b981' : '#ef4444',
            backgroundColor:
              projectionResult.impact >= 0
                ? 'rgba(16, 185, 129, 0.1)'
                : 'rgba(239, 68, 68, 0.1)',
            fill: true,
            tension: 0.4,
            pointRadius: 0,
            pointHoverRadius: 4,
          },
        ],
      }
    : null

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 800,
      easing: 'easeOutQuart' as const,
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
        },
      },
      tooltip: {
        backgroundColor: '#18181b',
        titleColor: '#fff',
        bodyColor: '#fff',
        padding: 12,
        callbacks: {
          label: (context: { dataset: { label?: string }; parsed: { y: number | null } }) =>
            `${context.dataset.label}: ${formatCurrency(context.parsed.y ?? 0)}`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#71717a' },
      },
      y: {
        grid: { color: 'rgba(156, 163, 175, 0.1)' },
        grace: '5%',
        ticks: {
          color: '#71717a',
          callback: (value: number | string) => {
            const num = Number(value)
            if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`
            if (num >= 1000) return `$${(num / 1000).toFixed(0)}K`
            return `$${num}`
          },
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Scenario Analysis
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Explore "what-if" scenarios and see how they impact your net worth
          </p>
        </div>
      </div>

      {/* Onboarding Modal */}
      {showOnboarding && (
        <ScenarioOnboarding
          onComplete={handleOnboardingComplete}
          onClose={() => setShowOnboarding(false)}
          suggestedExpense={monthlyExpense || 5000}
          viewMode={viewMode === 'individual' ? 'personal' : 'household'}
        />
      )}

      {/* Scenario Input - Always visible at top */}
      {!showOnboarding && (
        <Card className={projectionResult ? 'border-orange-200 dark:border-orange-800' : ''}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Sparkles className={`h-5 w-5 ${projectionResult ? 'text-orange-500' : 'text-purple-500'}`} />
                  {projectionResult ? 'Add Another Scenario' : 'Describe Your Scenario'}
                </CardTitle>
                <CardDescription>
                  {projectionResult
                    ? `Stack more "what-if" scenarios on top of "${currentSpec?.title}"`
                    : 'Use natural language to explore "what-if" scenarios'
                  }
                </CardDescription>
              </div>
              <div className="flex gap-2">
                {projectionResult && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setProjectionResult(null)
                      setCurrentSpec(null)
                      setStackedSpecs([])
                      setScenarioText('')
                      setExplanation(null)
                    }}
                  >
                    <X className="mr-2 h-4 w-4" />
                    Clear All
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setActiveTab(activeTab === 'saved' ? 'create' : 'saved')}
                >
                  <Save className="mr-2 h-4 w-4" />
                  Saved ({savedScenarios.length})
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowOnboarding(true)}
                >
                  <Settings className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder={projectionResult
                  ? "Add more: e.g., Also, what if I get a 20% raise?"
                  : "e.g., What if the stock market drops 30% and I lose my job for 6 months?"
                }
                value={scenarioText}
                onChange={(e) => setScenarioText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleParse()}
                className="flex-1"
              />
              <Button onClick={handleParse} disabled={isParsing || !scenarioText.trim()}>
                {isParsing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
                <span className="ml-2">{projectionResult ? 'Add' : 'Analyze'}</span>
              </Button>
            </div>

            {/* Personalized Suggestions */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                  <User className="h-3.5 w-3.5" />
                  {projectionResult ? 'Add more scenarios:' : 'Based on your finances:'}
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => loadSuggestions(true)}
                  disabled={isLoadingSuggestions}
                  className="text-xs h-7 px-2"
                >
                  {isLoadingSuggestions ? (
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  ) : (
                    <RefreshCw className="h-3 w-3 mr-1" />
                  )}
                  More ideas
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 min-h-[36px]">
                <AnimatePresence mode="wait">
                  {isLoadingSuggestions ? (
                    <motion.div
                      key="loading"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="flex flex-col gap-3 w-full"
                    >
                      <div className="flex items-center gap-2 text-sm text-primary">
                        <motion.div
                          animate={{ opacity: [0.4, 1, 0.4] }}
                          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                        >
                          <Sparkles className="h-4 w-4" />
                        </motion.div>
                        <span className="text-gray-600 dark:text-gray-400">
                          Personalizing scenarios based on your portfolio...
                        </span>
                      </div>
                      <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <motion.div
                            key={i}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: i * 0.1, duration: 0.3 }}
                          >
                            <Skeleton className="h-8 w-32 animate-pulse" />
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="suggestions"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="flex flex-wrap gap-2"
                    >
                      {suggestions.map((suggestion, index) => (
                        <motion.div
                          key={suggestion.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05, duration: 0.3 }}
                        >
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleUseTemplate({ id: suggestion.id, title: suggestion.title, description: suggestion.description, spec: suggestion.spec })}
                            className="text-xs border-primary/30 bg-primary/5 hover:bg-primary/10"
                            title={suggestion.description}
                          >
                            {suggestion.title}
                          </Button>
                        </motion.div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Templates */}
            <div className="space-y-2 pt-2 border-t">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {projectionResult ? 'Or add a template:' : 'Or try a template:'}
              </p>
              <div className="flex flex-wrap gap-2">
                {isLoadingTemplates ? (
                  <>
                    <Skeleton className="h-8 w-32" />
                    <Skeleton className="h-8 w-28" />
                    <Skeleton className="h-8 w-36" />
                  </>
                ) : (
                  templates.map((template) => (
                    <Button
                      key={template.id}
                      variant="outline"
                      size="sm"
                      onClick={() => handleUseTemplate(template)}
                      className="text-xs"
                    >
                      {template.title}
                    </Button>
                  ))
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Baseline Projection View */}
      {!showOnboarding && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                {projectionResult && stackedSpecs.length > 0 ? (
                  <>
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      {stackedSpecs.map((spec, index) => (
                        <Badge
                          key={index}
                          variant="secondary"
                          className="bg-orange-100 text-orange-700 hover:bg-orange-200 dark:bg-orange-900/30 dark:text-orange-300 text-sm py-1 px-2"
                        >
                          {spec.title}
                          <button
                            onClick={() => removeStackedScenario(index)}
                            className="ml-1.5 hover:text-orange-900 dark:hover:text-orange-100"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                    <CardDescription>
                      {currentSpec?.time_horizon_months}-month scenario projection
                    </CardDescription>
                  </>
                ) : (
                  <>
                    <CardTitle className="text-lg">Your Financial Baseline</CardTitle>
                    <CardDescription>
                      24-month projection based on your current finances
                    </CardDescription>
                  </>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowOnboarding(true)}
              >
                <Settings className="mr-2 h-4 w-4" />
                Edit Settings
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {isLoadingBaseline || isLoadingDefaults || isRunning ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                <span className="ml-3 text-gray-500">
                  {isRunning ? 'Analyzing scenario...' : 'Loading projection...'}
                </span>
              </div>
            ) : projectionResult ? (
              /* Show custom scenario results */
              <>
                {/* Stats Row for Scenario */}
                <div className="grid gap-4 sm:grid-cols-4">
                  <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                    <p className="text-xs text-gray-500">Current Net Worth</p>
                    <p className="text-xl font-bold">
                      {formatCurrency(projectionResult.starting_net_worth)}
                    </p>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                    <p className="text-xs text-gray-500">Baseline ({currentSpec?.time_horizon_months}mo)</p>
                    <p className="text-xl font-bold text-gray-600">
                      {formatCurrency(projectionResult.final_baseline)}
                    </p>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                    <p className="text-xs text-gray-500">Scenario ({currentSpec?.time_horizon_months}mo)</p>
                    <p className={`text-xl font-bold ${projectionResult.impact >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(projectionResult.final_scenario)}
                    </p>
                  </div>
                  <div className={`rounded-lg p-4 ${projectionResult.impact >= 0 ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
                    <p className="text-xs text-gray-500">Impact</p>
                    <div className="flex items-center gap-2">
                      {projectionResult.impact >= 0 ? (
                        <TrendingUp className="h-5 w-5 text-green-600" />
                      ) : (
                        <TrendingDown className="h-5 w-5 text-red-600" />
                      )}
                      <p className={`text-xl font-bold ${projectionResult.impact >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {projectionResult.impact >= 0 ? '+' : ''}{formatCurrency(projectionResult.impact)}
                      </p>
                    </div>
                    <p className="text-xs text-gray-500">
                      ({projectionResult.impact_pct >= 0 ? '+' : ''}{projectionResult.impact_pct.toFixed(1)}%)
                    </p>
                  </div>
                </div>

                {/* Scenario Chart */}
                <div className="h-64">
                  {chartData && <Line data={chartData} options={chartOptions} />}
                </div>

                {/* Assumptions */}
                {currentSpec && currentSpec.assumptions.length > 0 && (
                  <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Assumptions:</p>
                    <ul className="list-disc space-y-1 pl-5">
                      {currentSpec.assumptions.map((a, i) => (
                        <li key={i} className="text-sm text-gray-600 dark:text-gray-400">{a}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Explanation */}
                {explanation && (
                  <div className="rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20 border-l-4 border-blue-400">
                    <p className="text-sm text-blue-800 dark:text-blue-200">{explanation}</p>
                  </div>
                )}

                {/* Save Button */}
                <div className="flex justify-end">
                  <Button onClick={handleSave} disabled={isSaving}>
                    {isSaving ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="mr-2 h-4 w-4" />
                    )}
                    Save Scenario
                  </Button>
                </div>
              </>
            ) : baselineProjection ? (
              /* Show baseline projection */
              <>
                {/* Stats Row */}
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                    <p className="text-xs text-gray-500">Starting Net Worth</p>
                    <p className="text-xl font-bold">
                      {formatCurrency(baselineProjection.assumptions.starting_net_worth)}
                    </p>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                    <p className="text-xs text-gray-500">Monthly Savings</p>
                    <p className={`text-xl font-bold ${baselineProjection.assumptions.monthly_savings >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(baselineProjection.assumptions.monthly_savings)}/mo
                    </p>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                    <p className="text-xs text-gray-500">Est. Growth Rate</p>
                    <p className="text-xl font-bold text-blue-600">
                      ~{baselineProjection.assumptions.weighted_annual_return}%/yr
                    </p>
                  </div>
                </div>

                {/* Chart */}
                <div className="h-64">
                  <Line
                    data={{
                      labels: baselineProjection.projection.baseline_series.map((p: { month: number }) =>
                        p.month === 0 ? 'Now' : `${p.month}mo`
                      ),
                      datasets: [
                        {
                          label: 'Baseline',
                          data: baselineProjection.projection.baseline_series.map((p: { value: number }) => p.value),
                          borderColor: baselineProjection.assumptions.monthly_savings >= 0 ? 'rgb(34, 197, 94)' : 'rgb(239, 68, 68)',
                          backgroundColor: baselineProjection.assumptions.monthly_savings >= 0 ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                          fill: true,
                          tension: 0.4,
                        },
                        ...(scenarioProjection ? [{
                          label: 'With Scenarios',
                          data: scenarioProjection.scenario_series.map((p: { value: number }) => p.value),
                          borderColor: scenarioProjection.impact >= 0 ? 'rgb(34, 197, 94)' : 'rgb(239, 68, 68)',
                          backgroundColor: scenarioProjection.impact >= 0 ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                          fill: false,
                          borderDash: [5, 5],
                          tension: 0.4,
                        }] : []),
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: appliedFactors.length > 0 || scenarioProjection !== null },
                        tooltip: {
                          callbacks: {
                            label: (ctx) => `${ctx.dataset.label}: ${formatCurrency(ctx.parsed.y ?? 0)}`,
                          },
                        },
                      },
                      scales: {
                        y: {
                          ticks: {
                            callback: (value) => formatCurrency(value as number),
                          },
                        },
                      },
                    }}
                  />
                </div>

                {/* Projection Summary */}
                <div className="flex items-center justify-between rounded-lg bg-gradient-to-r from-purple-50 to-pink-50 p-4 dark:from-purple-900/20 dark:to-pink-900/20">
                  <div>
                    <p className="text-sm text-gray-500">In 24 months</p>
                    <p className="text-2xl font-bold">{formatCurrency(baselineProjection.projection.final_baseline)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Growth</p>
                    <p className="text-lg font-semibold text-green-600">
                      +{formatCurrency(baselineProjection.projection.final_baseline - baselineProjection.assumptions.starting_net_worth)}
                    </p>
                  </div>
                  {scenarioProjection && (
                    <div className="text-right border-l pl-4 ml-4">
                      <p className="text-sm text-gray-500">With Scenarios</p>
                      <p className="text-lg font-semibold text-orange-600">
                        {formatCurrency(scenarioProjection.final_scenario)}
                      </p>
                      <p className={`text-sm ${scenarioProjection.final_scenario >= baselineProjection.projection.final_baseline ? 'text-green-600' : 'text-red-600'}`}>
                        ({scenarioProjection.final_scenario >= baselineProjection.projection.final_baseline ? '+' : ''}{formatCurrency(scenarioProjection.final_scenario - baselineProjection.projection.final_baseline)} impact)
                      </p>
                    </div>
                  )}
                </div>

                {/* Applied Factors */}
                {appliedFactors.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Applied scenarios</p>
                    <div className="flex flex-wrap gap-2">
                      {appliedFactors.map((factor) => (
                        <Badge
                          key={factor.id}
                          variant="secondary"
                          className="bg-orange-100 text-orange-700 hover:bg-orange-200"
                        >
                          {factor.title}
                          <button
                            onClick={() => removeFactor(factor.id)}
                            className="ml-2 hover:text-orange-900"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <AlertCircle className="mx-auto h-8 w-8 mb-2" />
                <p>Unable to load projection</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => setShowOnboarding(true)}
                >
                  Configure Settings
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Error Display */}
      {error && (
        <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
          <CardContent className="flex items-center gap-2 py-4">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
          </CardContent>
        </Card>
      )}

      {/* Clarifying Questions */}
      {currentSpec?.needs_clarification && currentSpec.clarifying_questions.length > 0 && (
        <Card className="border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20">
          <CardHeader>
            <CardTitle className="text-yellow-800 dark:text-yellow-200">
              Confirm the details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {currentSpec.clarifying_questions.map((q, i) => {
              const isObjectQuestion = typeof q === 'object' && q !== null
              const questionText = isObjectQuestion ? q.question : q
              const suggestedValue = isObjectQuestion ? q.suggested_value : undefined
              const inputType = isObjectQuestion ? q.input_type : 'text'
              const currentValue = clarificationAnswers[i] ?? (suggestedValue !== undefined ? String(suggestedValue) : '')
              const isFundSourceQuestion = questionText.toLowerCase().includes('where') &&
                (questionText.toLowerCase().includes('money') || questionText.toLowerCase().includes('fund') || questionText.toLowerCase().includes('come from'))

              const fundSourceOptions = [
                { value: 'cash', label: 'Cash', icon: '💵' },
                { value: 'us_equity', label: 'US Stocks', icon: '📈' },
                { value: 'intl_equity', label: 'Intl Stocks', icon: '🌍' },
                { value: 'bonds', label: 'Bonds', icon: '📊' },
                { value: 'real_estate', label: 'Real Estate', icon: '🏠' },
                { value: 'crypto', label: 'Crypto', icon: '₿' },
              ]

              const getInputPrefix = () => inputType === 'currency' ? '$' : ''
              const getInputSuffix = () => {
                if (inputType === 'months') return ' months'
                if (inputType === 'percent') return '%'
                return ''
              }

              return (
                <div key={i} className="space-y-2">
                  <label className="text-sm font-medium text-yellow-700 dark:text-yellow-300">
                    {questionText}
                  </label>
                  {isFundSourceQuestion ? (
                    <div className="flex flex-wrap gap-2">
                      {fundSourceOptions.map((option) => (
                        <Button
                          key={option.value}
                          variant={currentValue === option.value ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setClarificationAnswers((prev) => ({ ...prev, [i]: option.value }))}
                          className={currentValue === option.value
                            ? 'bg-yellow-600 hover:bg-yellow-700'
                            : 'border-yellow-300 hover:bg-yellow-100 dark:border-yellow-700 dark:hover:bg-yellow-900/50'}
                        >
                          <span className="mr-1">{option.icon}</span>
                          {option.label}
                        </Button>
                      ))}
                    </div>
                  ) : (
                    <div className="relative">
                      {getInputPrefix() && (
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">{getInputPrefix()}</span>
                      )}
                      <Input
                        type={['currency', 'number', 'months', 'percent'].includes(inputType) ? 'number' : 'text'}
                        placeholder={suggestedValue !== undefined ? String(suggestedValue) : 'Enter your answer...'}
                        value={currentValue}
                        onChange={(e) => setClarificationAnswers((prev) => ({ ...prev, [i]: e.target.value }))}
                        className={`bg-white dark:bg-gray-800 ${getInputPrefix() ? 'pl-7' : ''} ${getInputSuffix() ? 'pr-16' : ''}`}
                      />
                      {getInputSuffix() && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">{getInputSuffix()}</span>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
            <div className="flex gap-2 pt-2">
              <Button onClick={handleRefineWithAnswers} disabled={isRefining}>
                {isRefining ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
                Continue
              </Button>
              <Button variant="outline" onClick={() => currentSpec && runProjection(currentSpec)} disabled={isRunning}>
                Skip & Run Anyway
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Saved Scenarios (collapsible) */}
      {activeTab === 'saved' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Save className="h-5 w-5" />
                Saved Scenarios
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setActiveTab('create')}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {isLoadingSaved ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : savedScenarios.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No saved scenarios yet</p>
            ) : (
              <div className="space-y-3">
                {savedScenarios.map((scenario) => (
                  <div key={scenario.id} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-800">
                    <div className="space-y-1">
                      <h3 className="font-medium text-gray-900 dark:text-gray-100">{scenario.title}</h3>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs">{scenario.spec.time_horizon_months} months</Badge>
                        <span className="text-xs text-gray-500">{new Date(scenario.updated_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleRunSaved(scenario)}>
                        <Play className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDelete(scenario.id)} className="text-red-500 hover:bg-red-50">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
